/**
 * Static per-service task checklists for the Cleaner Portal.
 * Each task has a stable `key` used for persistence in `job.checklist_progress`.
 * The list per service is used to render the tick-off UI on the /portal detail view.
 */

const KITCHEN = [
  { key: "kitchen_counters", label: "Wipe counters + backsplash" },
  { key: "kitchen_stovetop", label: "Clean stovetop + hood" },
  { key: "kitchen_sink", label: "Scrub sink + polish taps" },
  { key: "kitchen_appliances", label: "Wipe outer appliances" },
  { key: "kitchen_floor", label: "Mop kitchen floor" },
];

const BATHROOMS = [
  { key: "bath_toilet", label: "Disinfect toilet inside + out" },
  { key: "bath_shower", label: "Scrub shower / bathtub" },
  { key: "bath_sink", label: "Clean sink + mirror" },
  { key: "bath_floor", label: "Mop bathroom floor" },
  { key: "bath_supplies", label: "Restock supplies (if provided)" },
];

const LIVING = [
  { key: "living_dust", label: "Dust all surfaces + shelves" },
  { key: "living_vacuum", label: "Vacuum carpets + upholstery" },
  { key: "living_floor", label: "Mop hard floors" },
  { key: "living_trash", label: "Empty bins + replace liners" },
];

const BEDROOMS = [
  { key: "bed_make", label: "Make beds + fluff pillows" },
  { key: "bed_dust", label: "Dust nightstands + décor" },
  { key: "bed_floor", label: "Vacuum / mop floors" },
];

const DEEP_EXTRAS = [
  { key: "deep_baseboards", label: "Wipe baseboards + door frames" },
  { key: "deep_switches", label: "Wipe light switches + handles" },
  { key: "deep_vents", label: "Dust vents + ceiling fans" },
  { key: "deep_windows_inside", label: "Clean interior window glass" },
];

const MOVE_EXTRAS = [
  { key: "move_cabinets", label: "Wipe inside all cabinets + drawers" },
  { key: "move_appliances_inside", label: "Clean inside oven + fridge + microwave" },
  { key: "move_walls", label: "Spot-wash walls" },
  { key: "move_final_walkthrough", label: "Final walkthrough with customer / photos" },
];

const AIRBNB_EXTRAS = [
  { key: "airbnb_linen", label: "Change bed linens + towels" },
  { key: "airbnb_amenities", label: "Restock welcome amenities" },
  { key: "airbnb_photos", label: "Photograph finished rooms for host" },
];

const OFFICE = [
  { key: "office_desks", label: "Wipe + sanitize desks" },
  { key: "office_common", label: "Kitchen / break room clean" },
  { key: "office_restrooms", label: "Restrooms full clean + restock" },
  { key: "office_bins", label: "Empty all bins" },
  { key: "office_floors", label: "Vacuum / mop all floors" },
];

const WINDOW = [
  { key: "window_frames", label: "Wipe frames + sills" },
  { key: "window_inside", label: "Clean interior glass" },
  { key: "window_outside", label: "Clean exterior glass" },
];

const POST_CONSTRUCTION = [
  { key: "pc_debris", label: "Remove construction debris + dust" },
  { key: "pc_surfaces", label: "Deep-wipe all surfaces" },
  { key: "pc_fixtures", label: "Detail-clean fixtures + tiles" },
  { key: "pc_windows", label: "Clean windows + frames" },
  { key: "pc_final", label: "Final walkthrough + touch-ups" },
];

const GARDEN = [
  { key: "garden_debris", label: "Remove leaves / debris" },
  { key: "garden_furniture", label: "Wipe outdoor furniture" },
  { key: "garden_sweep", label: "Sweep patio / terrace" },
];

const PRESSURE = [
  { key: "pressure_prep", label: "Prep surface + protect edges" },
  { key: "pressure_wash", label: "Pressure wash target areas" },
  { key: "pressure_rinse", label: "Final rinse + inspect" },
];

const CHECKLISTS_BY_SERVICE = {
  "Regular Cleaning": [...KITCHEN, ...BATHROOMS, ...LIVING, ...BEDROOMS],
  "Regular/Maintenance Cleaning": [...KITCHEN, ...BATHROOMS, ...LIVING, ...BEDROOMS],
  "Deep Cleaning": [...KITCHEN, ...BATHROOMS, ...LIVING, ...BEDROOMS, ...DEEP_EXTRAS],
  "Move-In / Move-Out Cleaning": [...KITCHEN, ...BATHROOMS, ...LIVING, ...BEDROOMS, ...MOVE_EXTRAS],
  "Move-In/Out": [...KITCHEN, ...BATHROOMS, ...LIVING, ...BEDROOMS, ...MOVE_EXTRAS],
  "Airbnb Cleaning": [...KITCHEN, ...BATHROOMS, ...LIVING, ...BEDROOMS, ...AIRBNB_EXTRAS],
  "Office / Commercial Cleaning": OFFICE,
  "Office Cleaning": OFFICE,
  "Window Cleaning": WINDOW,
  "Post-Construction Cleaning": POST_CONSTRUCTION,
  "Post-Renovation": POST_CONSTRUCTION,
  "Garden / Terrace Cleaning": GARDEN,
  "Garden/Terrace": GARDEN,
  "Pressure Washing": PRESSURE,
};

export function getChecklistFor(serviceType) {
  return CHECKLISTS_BY_SERVICE[serviceType] || [...KITCHEN, ...BATHROOMS, ...LIVING];
}
